/**
 * Created by shanie on 11/3/16.
 */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

// Components
import RequestShippingQuoteModal from 'dibs-request-shipping-modal'; // eslint-disable-line no-unused-vars
import Header from '../Headers/EditInlineHeader';
import Modals from '../Modals/Modals';
import Recommendations from '../Recommendations/Recommendations';
import SearchResults from '../Headers/SearchResults';
import ItemsRefetchWrapper from '../Items/ItemsRefetchWrapper';
import Toolbar from '../Toolbar/Toolbar';

// Helpers
import actions from '../../actions/FavoritesActions';
import classnames from 'classnames';
import get from 'lodash.get';
import {
    copy,
    cutoffs,
    deviceTypes,
    modals,
    mutations,
    portfolioTypes,
    sortBy,
    tabs,
} from '../../constants';
import { resetSearchParams } from '../../helpers/urlHelpers';

// Tracking
import trackPageView from '../../helpers/trackPageView';
import trackFavoritesEvent from '../../helpers/trackFavoritesEvent';

// Mutations
import ModifyPortfolioItems from '../../mutations/ModifyPortfolioItems';
import ModifyPortfolios from '../../mutations/ModifyPortfolios';

// Styles
import styles from '../Items/styles/ItemsContainer.scss';

const nullResponse = mutations.responses.NULL;
const refetchOptions = { force: true };

export class FolderContainerComponent extends React.Component {
    constructor(props) {
        super(props);

        this.isPortfolioMissmatch = this.isPortfolioMissmatch.bind(this);
        this.folderMutations = this.folderMutations.bind(this);
        this.handleMutationResponse = this.handleMutationResponse.bind(this);
        this.itemMutations = this.itemMutations.bind(this);
        this.loadItems = this.loadItems.bind(this);
        this.resetUiState = this.resetUiState.bind(this);
    }

    componentWillMount() {
        const portfolioNode = this.getPortfolioNode(this.props);
        if (portfolioNode) {
            this.props.setPortfolioName(portfolioNode.name);
        }

        if (this.props.tabChanged) {
            this.props.setFullPageLoading(true);
            this.props.resetTotalResults();
            this.props.setSortBy(sortBy.CUSTOM);
            // Need to forceFetch after every transition from null-state page
            // because if the folder tab container is rendering, it means that the user
            // engaged with the null-state page and added an item to their folder
            const refetchVariables = fragmentVariables => ({
                ...fragmentVariables,
                useProjectEndpoint: this.props.tab === tabs.PROJECT,
            });
            this.props.relay.refetch(refetchVariables, null, () => {
                if (this.props.openModalType === modals.REMOVE_FOLDERS) {
                    this.props.setOpenModalType('');
                }
                this.props.setFullPageLoading(false);
                this.props.setMutationResponse('');
            });
        }
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        trackPageView(this.props.viewMode);
    }

    componentWillReceiveProps(nextProps) {
        const isNextResponseNull =
            this.props.mutationResponse !== nullResponse &&
            nextProps.mutationResponse === nullResponse;
        const isSortByChanged = this.props.sortBy !== nextProps.sortBy;

        if (isNextResponseNull || isSortByChanged) {
            this.props.setFullPageLoading(true);
            const refetchVariables = fragmentVariables => ({
                ...fragmentVariables,
                sortBy: nextProps.sortBy,
            });
            this.props.relay.refetch(
                refetchVariables,
                null,
                () => {
                    this.props.setFullPageLoading(false);
                },
                refetchOptions
            );
        }
    }

    componentDidUpdate(prevProps) {
        const havePrevTotalResults = prevProps.totalResults >= 0;
        const haveTotalResults = this.props.totalResults >= 0;
        if (
            !havePrevTotalResults &&
            haveTotalResults &&
            this.props.mutationResponse === nullResponse
        ) {
            this.props.setOpenModalType(modals.ADD_ITEMS);
            this.props.setShowErrorSuccessModal(true);
            this.props.setModalOpen(true);
        }
    }

    getPortfolioNode(props = {}) {
        const { viewer } = props;
        return get(viewer, 'project.portfolio') || get(viewer, 'folderPortfolios.edges[0].node');
    }

    isPortfolioMissmatch() {
        const { portfolioIds, tab, viewer } = this.props;
        const showProjects = get(viewer, 'user.isVerifiedTrade');
        const projectPortfolioId = get(viewer, 'project.portfolio.serviceId');
        const passedPortfolioId = portfolioIds && portfolioIds[0];
        const projectPortfolioMismatch =
            tab === tabs.PROJECT && projectPortfolioId !== passedPortfolioId;

        return !showProjects || projectPortfolioMismatch;
    }

    handleMutationResponse(action, response) {
        // Need to forceFetch after every mutation so that post-mutation
        // back-end changes are visually reflected in UI
        this.props.relay.refetch(
            fragmentVariables => fragmentVariables,
            null,
            () => {
                if (action === mutations.types.REORDER) {
                    this.props.setReordered(false);
                } else {
                    if (action === mutations.types.SHARE) {
                        this.props.resetEmailState();
                    } else {
                        this.props.setMutationResponse(response);
                        this.props.setModalOpen(true);
                        this.props.setShowErrorSuccessModal(true);
                    }
                    this.props.selectItems([]);
                    this.props.setSelectMode(false);
                }
                this.props.setFullPageLoading(false);
            },
            refetchOptions
        );
    }

    loadItems(first) {
        this.props.setFullPageLoading(true);
        const refetchVariables = fragmentVariables => ({
            ...fragmentVariables,
            first,
        });
        this.props.relay.refetch(refetchVariables, null, () => {
            this.props.setFullPageLoading(false);
            this.props.setNumItems(first);
        });
    }

    itemMutations({
        action,
        targetPortfolioId,
        targetPortfolioItemId,
        startPortfolioItemId,
        endPortfolioItemId,
        note,
    }) {
        this.props.setFullPageLoading(true);
        this.props.setMutationResponse('');
        this.props.setModalOpen(false);
        this.props.setShowErrorSuccessModal(false);

        if (action === mutations.types.REORDER) {
            this.loadItems(cutoffs.ADDITIONAL_ITEMS);
        }
        const portfoliosEdge = get(this.props, 'viewer.folderPortfolios.edges[0]') || {};
        ModifyPortfolioItems.commit(this.props.relay.environment, {
            action,
            endPortfolioItemId,
            itemsIds: this.props.selectedItems.map(item => item.itemId),
            note,
            portfolioItemsIds: this.props.selectedItems.map(item => item.portfolioItemId),
            portfolioId: targetPortfolioId || get(this.getPortfolioNode(this.props), 'serviceId'),
            portfoliosEdge,
            startPortfolioItemId,
            targetPortfolioItemId,
            userId: get(this.props, 'viewer.user.serviceId'),
        })
            .then(() => {
                trackFavoritesEvent({
                    action,
                    setShouldTrackReordering: this.props.setShouldTrackReordering,
                    shouldTrackReordering: this.props.shouldTrackReordering,
                });
                this.handleMutationResponse(action, mutations.responses.SUCCESS);
            })
            .catch(() => {
                this.handleMutationResponse(action, mutations.responses.ERROR);
            });
    }

    folderMutations({ ...args }) {
        if (args.action === mutations.types.REMOVE) {
            this.props.setMutationResponse('');
            this.props.setFullPageLoading(true);
            this.props.setModalOpen(false);
            this.props.setShowErrorSuccessModal(false);
        }
        const portfoliosEdge = get(this.props, 'viewer.folderPortfolios.edges[0]') || {};
        ModifyPortfolios.commit(this.props.relay.environment, {
            ...args,
            portfoliosType: 'FOLDER',
            portfolioIds: [get(portfoliosEdge, 'node.serviceId')],
            portfoliosEdge,
            userId: get(this.props, 'viewer.user.serviceId'),
        })
            .then(() => {
                if (args.action === mutations.types.REMOVE) {
                    trackFavoritesEvent({
                        action: args.action,
                        type: tabs.FOLDER,
                    });
                    this.props.setPortfolioTypes([portfolioTypes.FOLDER]);
                    this.props.setTabChanged(true);
                    this.props.setTab(tabs.FOLDERS);
                }
            })
            .catch(() => {
                this.handleMutationResponse(args.action, mutations.responses.ERROR);
            });
    }

    resetUiState() {
        this.props.resetUiState();
        resetSearchParams();
    }

    render() {
        const {
            deviceType,
            first,
            hasUserId,
            launchModal,
            openModalType,
            relay,
            selectedItems,
            selectedItemIds,
            selecting,
            searchTerm,
            showProjects,
            stickyToolbar,
            tab,
            viewer,
            isPublic,
            totalResults,
            viewMode,
        } = this.props;

        const stickyClasses = classnames(styles.container, {
            [styles.sticky]: stickyToolbar && deviceType !== deviceTypes.MOBILE,
            [styles.slideIn]: selecting,
        });

        const selectOptions = [
            { label: copy.sortBy.CUSTOM, value: sortBy.CUSTOM },
            { label: copy.sortBy.PRICE_ASC, value: sortBy.PRICE_ASC },
            { label: copy.sortBy.PRICE_DESC, value: sortBy.PRICE_DESC },
            { label: copy.sortBy.RECENTLY_CREATED, value: sortBy.RECENTLY_CREATED },
        ];

        const portfolioNode = this.getPortfolioNode(this.props);
        const totalPortfolioItems = get(portfolioNode, 'totalPortfolioItems.totalResults');
        const portfolioName = (portfolioNode && portfolioNode.name) || '';
        const portfolioServiceId = (portfolioNode && portfolioNode.serviceId) || '';
        const description = (portfolioNode && portfolioNode.description) || '';
        const userId = get(viewer, 'user.serviceId');
        const isProjectTab = [tabs.PROJECT, tabs.PROJECTS].includes(tab);

        if (isProjectTab && this.isPortfolioMissmatch()) {
            return null;
        }

        const headerSharedProps = {
            isPublic,
            description,
            selecting,
            title: portfolioName,
            totalResults: totalPortfolioItems,
            onEditFolder: (...args) => this.folderMutations(...args),
            onEditPrivacy: (...args) => this.folderMutations(...args),
            onRemoveFolder: () => launchModal(modals.REMOVE_FOLDERS),
            onShare: () => launchModal(modals.SHARE_FOLDER),
        };

        if (totalPortfolioItems === 0 && searchTerm === '') {
            return (
                <Fragment>
                    <Header {...headerSharedProps} />
                    <Recommendations
                        favoriteItemOnLoad={false}
                        hasUserId={hasUserId}
                        portfolioId={portfolioServiceId}
                        selectedItemIds={selectedItemIds}
                        trending={viewer}
                        userId={userId}
                        viewer={viewer}
                    />
                    <Modals
                        relay={relay}
                        folderMutations={(...args) => this.folderMutations(...args)}
                        modalOpen={this.props.modalOpen}
                        mutationResponse={this.props.mutationResponse}
                        openModalType={this.props.openModalType}
                        showErrorSuccessModal={this.props.showErrorSuccessModal}
                    />
                </Fragment>
            );
        } else {
            return (
                <Fragment>
                    <Header
                        {...headerSharedProps}
                        isLoadingFullPage={this.props.isLoadingFullPage}
                    />
                    {isPublic ? null : (
                        <div className={stickyClasses}>
                            <Toolbar
                                relay={relay}
                                openModalType={openModalType}
                                portfolioId={portfolioServiceId}
                                removeItems={() => launchModal(modals.REMOVE_ITEMS)}
                                shareItems={() => launchModal(modals.SHARE_ITEMS)}
                                showSearchBar
                                selectedItemIds={selectedItemIds}
                                selectedItems={selectedItems}
                                selectOptions={selectOptions}
                                title={this.props.newPortfolioName || portfolioName}
                                totalItems={totalPortfolioItems}
                                userId={userId}
                                viewer={this.props.viewer}
                                viewMode={viewMode}
                            />
                        </div>
                    )}
                    {stickyToolbar && deviceType !== deviceTypes.MOBILE ? (
                        <div className={styles.shadow} />
                    ) : null}
                    {totalPortfolioItems > cutoffs.MAX_ITEMS && !isPublic ? (
                        <div className={styles.cutoff}>{copy.cuttoffs.FOLDER}</div>
                    ) : null}
                    {searchTerm !== '' && !this.props.isLoadingFullPage && (
                        <SearchResults
                            onClick={this.resetUiState}
                            searchTerm={searchTerm}
                            totalResults={totalResults}
                        />
                    )}
                    <ItemsRefetchWrapper
                        addNote={() => launchModal(modals.ADD_NOTE)}
                        onDrop={(...args) => this.itemMutations(...args)}
                        first={first}
                        isPublic={isPublic}
                        reordered={this.props.reordered}
                        reordering={this.props.reordering}
                        searchTerm={this.props.searchTerm}
                        showDealerBranding={this.props.showDealerBranding}
                        showSeller={this.props.showDealerBranding}
                        sortBy={this.props.sortBy}
                        portfolioNode={portfolioNode || null}
                        showProjects={showProjects}
                        type={tabs.FOLDER}
                        user={viewer ? viewer.user : null}
                        viewer={viewer || null}
                        viewMode={viewMode}
                    />
                    <Modals
                        relay={relay}
                        viewer={viewer}
                        handleMutationResponse={this.handleMutationResponse}
                        selectedItems={this.props.selectedItems}
                        portfolioIds={this.props.portfolioIds}
                        userEmail={get(viewer, 'user.profile.email')}
                        folderMutations={(...args) => this.folderMutations(...args)}
                        itemMutations={this.itemMutations}
                        modalOpen={this.props.modalOpen}
                        mutationResponse={this.props.mutationResponse}
                        openModalType={this.props.openModalType}
                        showErrorSuccessModal={this.props.showErrorSuccessModal}
                        isPublic={isPublic}
                    />
                </Fragment>
            );
        }
    }
}

FolderContainerComponent.propTypes = {
    deviceType: PropTypes.string,
    first: PropTypes.number,
    sortBy: PropTypes.string,
    isLoadingFullPage: PropTypes.bool,
    hasUserId: PropTypes.bool,
    launchModal: PropTypes.func,
    modalOpen: PropTypes.bool,
    mutationResponse: PropTypes.string,
    newPortfolioName: PropTypes.string,
    numItems: PropTypes.number,
    openModalType: PropTypes.string,
    portfolioIds: PropTypes.array,
    portfoliosConnection: PropTypes.object,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    reordering: PropTypes.bool,
    resetEmailState: PropTypes.func,
    resetUiState: PropTypes.func,
    resetTotalResults: PropTypes.func,
    searchTerm: PropTypes.string,
    selectItems: PropTypes.func,
    selectedItemIds: PropTypes.array,
    selectedItems: PropTypes.array,
    selecting: PropTypes.bool,
    setFullPageLoading: PropTypes.func,
    setModalOpen: PropTypes.func,
    setMutationResponse: PropTypes.func,
    setPortfolioName: PropTypes.func,
    setPortfolioPrivacy: PropTypes.func,
    setNumItems: PropTypes.func,
    setOpenModalType: PropTypes.func,
    setPortfolioViewPermission: PropTypes.func,
    setPortfolioTypes: PropTypes.func,
    setReordered: PropTypes.func,
    setSelectMode: PropTypes.func,
    setShouldTrackReordering: PropTypes.func,
    setShowErrorSuccessModal: PropTypes.func,
    setTabChanged: PropTypes.func,
    setStickToolbar: PropTypes.func,
    setSortBy: PropTypes.func,
    setTab: PropTypes.func,
    setViewingPublicVersion: PropTypes.func,
    showDealerBranding: PropTypes.bool,
    showErrorSuccessModal: PropTypes.bool,
    stickyToolbar: PropTypes.bool,
    tabChanged: PropTypes.bool,
    totalResults: PropTypes.number,
    shouldTrackReordering: PropTypes.bool,
    showProjects: PropTypes.bool,
    tab: PropTypes.string,
    userId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    viewMode: PropTypes.string,
    isPublic: PropTypes.bool,
};

const mapStateToProps = state => {
    return {
        deviceType: state.favorites.uiState.deviceType,
        sortBy: state.favorites.uiState.folderSort,
        modalOpen: state.favorites.modalState.modalOpen,
        mutationResponse: state.favorites.uiState.mutationResponse,
        openModalType: state.favorites.modalState.openModalType,
        newPortfolioName: state.favorites.creationState.newPortfolioName,
        portfolioIds: state.favorites.uiState.portfolioIds,
        reordering: state.favorites.uiState.reordering,
        selectedItems: state.favorites.uiState.selectedItems,
        selecting: state.favorites.uiState.selecting,
        stickyToolbar: state.favorites.uiState.stickToolbar,
        tabChanged: state.favorites.uiState.tabChanged,
        shouldTrackReordering: state.favorites.uiState.shouldTrackReordering,
        showErrorSuccessModal: state.favorites.modalState.showErrorSuccessModal,
        tab: state.favorites.uiState.tab,
        totalResults: state.favorites.uiState.totalResults,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetEmailState: () => {
            dispatch(actions.resetEmailState());
        },
        resetUiState: () => {
            dispatch(actions.resetUiState());
        },
        resetTotalResults: () => {
            dispatch(actions.setTotalResults({ totalResults: -1 }));
        },
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setModalOpen: modalOpen => {
            dispatch(actions.setModalOpen({ modalOpen }));
        },
        setMutationResponse: mutationResponse => {
            dispatch(actions.setMutationResponse({ mutationResponse }));
        },
        setPortfolioName: portfolioName => {
            dispatch(actions.setPortfolioName({ portfolioName }));
        },
        setPortfolioPrivacy: newPortfolioPrivacy => {
            dispatch(actions.setPortfolioPrivacy({ newPortfolioPrivacy }));
        },
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setOpenModalType: openModalType => {
            dispatch(actions.setOpenModalType({ openModalType }));
        },
        setPortfolioTypes: types => {
            dispatch(actions.setPortfolioTypes({ portfolioTypes: types }));
        },
        setPortfolioViewPermission: portfolioViewPermission => {
            dispatch(actions.setPortfolioViewPermission({ portfolioViewPermission }));
        },
        setReordered: reordered => {
            dispatch(actions.setReordered({ reordered }));
        },
        setSelectMode: selecting => {
            dispatch(actions.setSelectMode({ selecting }));
        },
        setShowErrorSuccessModal: showErrorSuccessModal => {
            dispatch(actions.setShowErrorSuccessModal({ showErrorSuccessModal }));
        },
        setShouldTrackReordering: shouldTrackReordering => {
            dispatch(actions.setShouldTrackReordering({ shouldTrackReordering }));
        },
        setStickToolbar: stickToolbar => {
            dispatch(actions.setStickToolbar({ stickToolbar }));
        },
        setTab: tab => {
            dispatch(actions.setTab({ tab }));
        },
        setTabChanged: tabChanged => {
            dispatch(actions.setTabChanged({ tabChanged }));
        },
        setViewingPublicVersion: isPublic => {
            dispatch(actions.setViewingPublicVersion({ isPublic }));
        },
        setSortBy: sortBySelection => {
            dispatch(actions.setSortBy({ sortBySelection }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(FolderContainerComponent);

const FolderContainer = createRefetchContainer(
    ReduxWrapped,
    {
        viewer: graphql`
            fragment FolderContainer_viewer on Viewer {
                ...Recommendations_trending
                ...RequestShippingQuoteModal_viewer
                ...Tiles_viewer
                id
                user(userId: $userId) @include(if: $hasUserId) {
                    serviceId
                    isVerifiedTrade
                    profile {
                        currency
                        email
                    }
                }
                folderPortfolios: portfolios(first: 1, portfolioIds: $portfolioIds)
                    @skip(if: $useProjectEndpoint) {
                    edges {
                        node {
                            id
                            serviceId
                            name
                            description
                            isPublic
                            ownerId
                            totalPortfolioItems: items(first: 1) {
                                totalResults
                            }
                            ...ItemsRefetchWrapper_portfolioNode
                        }
                    }
                }
                project(id: $projectId) @include(if: $useProjectEndpoint) {
                    portfolio {
                        id
                        serviceId
                        name
                        description
                        isPublic
                        ownerId
                        totalPortfolioItems: items(first: 1) {
                            totalResults
                        }
                        ...ItemsRefetchWrapper_portfolioNode
                    }
                }
            }
        `,
    },
    graphql`
        query FolderContainerRefetchQuery(
            $first: Int!
            $after: String
            $hasUserId: Boolean!
            $portfolioIds: [String]
            $projectId: String!
            $searchTerm: String
            $sortBy: PortfolioItemsSortByTypes
            $loadPortfolioData: Boolean!
            $selectedItemIds: [String]!
            $useProjectEndpoint: Boolean!
            $userId: String!
            $userIds: [String]!
            $itemId: String!
            $shouldFetchItem: Boolean!
            $showSeller: Boolean!
            $itemIds: [String]
            $shouldFetchConversation: Boolean!
            $shouldFetchTransaction: Boolean!
            $zipCode: String!
            $country: String!
            $canCheckZipCode: Boolean!
            $conversationId: ID!
            $hasConversationId: Boolean!
            $isTrade: Boolean!
            $page: Int!
        ) {
            viewer {
                ...FolderContainer_viewer
            }
        }
    `
);

export default FolderContainer;
